<template>
    <div
        :class="{
            'sticky-node': hasChildren,
            'tree-node-with-branch': level === 0 || !isLast,
            'tree-node-with-branch-last': level !== 0 && isLast
        }"
        :style="{ background, width: customWidth }"
        class="relative overflow-visible">
        <!-- Vertical branch line -->
        <div class="branch-vertical" />

        <!-- Node header -->
        <div class="node-header flex items-center gap-2 p-2" :style="{ zIndex: zIndexHeader }">
            <div class="branch-horizontal"></div>

            <TreeCheckbox
                :checked="isNodeChecked"
                :partial="areChildrenPartiallySelected"
                :disabled="!editMode && !disableCreateAndEdit"
                :readonly="readonly"
                :error="isError"
                @change="toggleSelected" />

            <ChevronDownIcon v-if="hasChildren && !collapsed" class="h-4 w-4 cursor-pointer" @click="toggleCollapse" />
            <ChevronRightIcon v-else-if="hasChildren && collapsed" class="h-4 w-4 cursor-pointer" @click="toggleCollapse" />

            <div class="flex-1 items-center ml-2">
                <div v-if="!isEditing || !editMode || disableCreateAndEdit" :class="isError ? 'text-red-500' : 'text-gray-700'">{{ node.name }}</div>
                <input
                    v-else
                    v-model="node.name"
                    v-sanitize
                    v-sanitize-csv
                    @keydown.enter.prevent="finishEdit"
                    aria-label="input"
                    @input="debouncedDuplicate"
                    :class="[
                        `block w-full appearance-none focus:outline-hidden focus:border-gray-300 rounded-md border px-2 py-1 placeholder-gray-400 shadow-sm sm:text-sm`,
                        isError ? 'border-red-500 focus:border-red-500' : 'border-gray-300'
                    ]"
                    placeholder="* Name - cannot be null, duplicated or contain * (wildcard)"
                    class="w-full border rounded px-2 py-1" />
            </div>

            <XMarkIcon v-if="editMode && !disableCreateAndEdit" class="h-5 w-5 text-red-400 cursor-pointer" @click="removeNode(parent, node)" />
            <button v-if="editMode && !disableCreateAndEdit" @click="isEditing ? finishEdit() : startEdit()" class="text-xs underline ml-2">
                {{ isEditing ? 'Done' : 'Edit' }}
            </button>
        </div>

        <div v-if="nextLevel && (!collapsed || !node.children?.length)" class="children-toolbar" :style="{ background, zIndex: zIndexToolbar }">
            <div class="text-sm font-medium text-gray-700">
                {{ node.children?.length || 0 }} {{ nextLevel.schemaLevel }}{{ (node.children?.length || 0) !== 1 ? 's' : '' }}
            </div>

            <button v-if="editMode && !disableCreateAndEdit" class="btn-add" @click.prevent="addChild">
                <PlusIcon class="h-4 w-4 mr-1" /> Add {{ nextLevel.schemaLevel }}
            </button>

            <button v-if="editMode && !disableCreateAndEdit" class="btn-add" @click="openFileUpload">
                <ArrowUpOnSquareStackIcon class="h-4 w-4 mr-1" /> Import .CSV
            </button>
            <input ref="fileInput" type="file" accept=".csv" class="hidden" @change="onCsvChange" />
        </div>
    </div>
</template>
<script>
import { PlusIcon, ChevronRightIcon, ChevronDownIcon, XMarkIcon, ArrowUpOnSquareStackIcon } from '@heroicons/vue/24/outline';
import TreeCheckbox from './TreeCheckbox.vue';
import Papa from 'papaparse';
import { debounce } from 'lodash';

export default {
    name: 'TreeNode',
    components: {
        TreeCheckbox,
        PlusIcon,
        ChevronRightIcon,
        ChevronDownIcon,
        XMarkIcon,
        ArrowUpOnSquareStackIcon
    },
    props: {
        node: { type: Object, required: true },
        parent: { type: Array, required: true },
        collapsed: { type: Boolean, default: false },
        toggleCollapse: { type: Function, default: () => {} },
        level: { type: Number, required: true },
        treeLevels: { type: Array, required: true },
        readonly: { type: Boolean, default: false },
        displayOnlySelected: { type: Boolean, default: false },
        editMode: { type: Boolean, default: false },
        disableCreateAndEdit: { type: Boolean, default: false },
        background: { type: String, default: 'white' },
        customWidth: { type: String, default: '95%' }
    },
    inject: ['visibleIds', 'sizeMap', 'computeSize', 'editingMap'],
    data() {
        return { fileInput: null };
    },
    computed: {
        hasChildren() {
            return this.node.children?.length > 0;
        },
        isLast() {
            return this.parent.indexOf(this.node) === this.parent.length - 1;
        },
        isNodeChecked() {
            return this.hasChildren ? this.node.children.some((c) => c.isSelected) : this.node.isSelected;
        },
        areChildrenPartiallySelected() {
            const tot = this.node.children?.length || 0;
            const sel = this.node.children?.filter((c) => c.isSelected).length || 0;
            return sel > 0 && sel < tot;
        },
        nextLevel() {
            const idx = this.treeLevels.findIndex((l) => l.schemaLevel === this.node.schemaLevel);
            return this.treeLevels[idx + 1] || null;
        },
        childLabel() {
            if (!this.nextLevel) return '';
            const lvl = this.nextLevel.schemaLevel.toLowerCase();
            return this.node.children.length === 1 ? lvl : lvl + 's';
        },
        zIndexHeader() {
            return Math.max(200 - this.level * 10, 0);
        },
        zIndexToolbar() {
            return this.zIndexHeader + 1;
        },
        isError() {
            // don’t ever show errors when not in edit mode
            if (!this.editMode || this.disableCreateAndEdit) {
                return false;
            }

            const name = (this.node.name || '').trim();
            // empty?
            if (!name) return true;
            // wildcard?
            if (name.includes('*')) return true;
            // duplicate within this level?
            if (this.parent.some((other) => other !== this.node && (other.name || '').trim() === name)) {
                return true;
            }
            return false;
        },
        isEditing() {
            return this.editingMap[this.node.id];
        }
    },
    mounted() {
        this.debouncedDuplicate.bind(this);
        this.fileInput = this.$refs.fileInput;
    },
    methods: {
        startEdit() {
            this.editingMap[this.node.id] = true;
        },
        finishEdit() {
            if (!this.isError) {
                this.editingMap[this.node.id] = false;
                this.$emit('duplicate'); // to re-check errors
            }
        },
        toggleCollapse() {
            this.toggleCollapse(this.node.id);
        },
        toggleSelected() {
            this.node.isSelected = !this.node.isSelected;

            if (this.hasChildren) {
                const recurse = (arr, sel) => {
                    for (const c of arr) {
                        if (this.visibleIds.has(c.id)) {
                            c.isSelected = sel;
                            if (c.children) recurse(c.children, sel);
                        }
                    }
                };
                recurse(this.node.children, this.node.isSelected);
            }

            this.$emit('duplicate');
            this.$emit('select-toggled');
        },
        removeNode(parentArr, node) {
            const idx = parentArr.indexOf(node);
            if (idx !== -1) {
                parentArr.splice(idx, 1);
                this.$emit('duplicate');
            }
        },
        debouncedDuplicate: debounce(function () {
            this.$emit('duplicate');
        }, 300),
        addChild() {
            if (!this.nextLevel) return;
            const entry = {
                name: '',
                isSelected: true,
                collapsed: true,
                schemaLevel: this.nextLevel.schemaLevel,
                schemaObject: this.nextLevel.schemaObject,
                children: [],
                placeholder: `Enter ${this.nextLevel.schemaLevel} name`
            };
            Object.defineProperty(entry, 'id', {
                value: crypto.randomUUID(),
                writable: false,
                enumerable: false
            });

            this.node.children = this.node.children || [];
            this.node.children.push(entry);
            this.sizeMap[entry.id] = this.computeSize(entry);
            this.editingMap[entry.id] = true;

            this.$emit('duplicate');
        },
        openFileUpload() {
            this.fileInput.value = '';
            this.fileInput.click();
        },
        async onCsvChange(e) {
            const file = e.target.files[0];
            if (!file?.name.toLowerCase().endsWith('.csv')) {
                alert('Please upload a valid CSV file.');
                return;
            }

            const batchSize = 1000;
            let buffer = [];

            const insertChildBatch = (names) => {
                const entries = names.map((name) => {
                    const node = {
                        name,
                        isSelected: true,
                        collapsed: true,
                        schemaLevel: this.nextLevel.schemaLevel,
                        schemaObject: this.nextLevel.schemaObject,
                        children: [],
                        placeholder: name
                    };
                    Object.defineProperty(node, 'id', {
                        value: crypto.randomUUID(),
                        writable: false,
                        enumerable: false
                    });
                    return node;
                });
                this.node.children = this.node.children || [];
                this.node.children.splice(this.node.children.length, 0, ...entries);
            };

            const vm = this;

            Papa.parse(file, {
                worker: true,
                skipEmptyLines: true,
                step({ data }) {
                    const name = data[0]?.trim();
                    if (name) buffer.push(name);
                    if (buffer.length >= batchSize) {
                        insertChildBatch(buffer);
                        buffer = [];
                    }
                },
                complete() {
                    if (buffer.length) insertChildBatch(buffer);
                    vm.$emit('duplicate');
                }
            });
        }
    }
};
</script>

<style scoped>
@reference '../style.css';

.branch-horizontal {
    width: 20px;
    height: 1px;
    background-color: #c0c0c0;
    position: absolute;
    left: 0;
    top: calc(50% - 0.5px);
}

.node-header {
    position: relative;
    padding: 8px;
}

.children-toolbar {
    top: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    background: inherit;
    border-bottom: 1px solid #c0c0c0;
    width: calc(100% - 2rem);
}

.children-list {
    border-bottom: 1px solid #c0c0c0;
    width: calc(100% - 1.5rem);
}

.tree-node-with-branch {
    border-left: 1px solid #c0c0c0;
    position: relative;
}
.tree-node-with-branch-last::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 50%;
    border-left: 1px solid #c0c0c0;
}

.sticky-node {
    position: sticky;
    top: 0;
    background: inherit;
}

.btn-add {
    @apply flex items-center px-2 py-1 border border-green-500 text-green-500 rounded-sm text-xs hover:bg-green-400 hover:text-white;
}

.node-container {
    box-sizing: border-box;
    position: relative;
    overflow: visible;
}
</style>
